import React, { useMemo, useState } from 'react';
import {
  Users, UserX, Check, LogIn, LogOut,
  Calendar, Search, RefreshCw, UserPlus, X, Clock, Filter
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const VISITOR_TYPES = [
  { value: 'visitor',  label: 'Visitor'          },
  { value: 'parent',   label: 'Parent/Guardian'  },
  { value: 'provider', label: 'Therapy Provider' },
  { value: 'other',    label: 'Other'            },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const fmtTimeOfDay = (value) => {
  const raw = (value || '').trim();
  if (!raw) return '';

  const twelve = raw.match(/^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AP]M)$/i);
  if (twelve) {
    const hour = String(parseInt(twelve[1], 10));
    const minute = twelve[2];
    const meridiem = twelve[3].toUpperCase();
    return `${hour}:${minute} ${meridiem} MST`;
  }

  const twentyFour = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFour) {
    const hour24 = parseInt(twentyFour[1], 10);
    const minute = twentyFour[2];
    const meridiem = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour12}:${minute} ${meridiem} MST`;
  }

  return `${raw} MST`;
};

const formatDateDisplay = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

const TYPE_LABEL = {
  staff: 'Staff', client: 'Client', parent: 'Parent/Guardian',
  provider: 'Therapy Provider', visitor: 'Visitor', other: 'Other',
};

const TYPE_BADGE = {
  staff:    'bg-indigo-100 text-indigo-700',
  client:   'bg-green-100 text-green-700',
  parent:   'bg-purple-100 text-purple-700',
  provider: 'bg-blue-100 text-blue-700',
  visitor:  'bg-gray-100 text-gray-600',
  other:    'bg-yellow-100 text-yellow-700',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function BuildingView({
  staff = [],
  clients = [],
  attendanceRecords = [],
  selectedDate,
  today,
  onDateChange,
  onCheckIn,
  onCheckOut,
  onRefresh,
  loading,
}) {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'clients'
  const [showAddVisitor, setShowAddVisitor] = useState(false);
  const [visitorForm, setVisitorForm] = useState({ name: '', type: 'visitor', host: '', organization: '' });
  const [addingVisitor, setAddingVisitor] = useState(false);
  const [pointInTime, setPointInTime] = useState(''); // HH:MM for historical "at this time" query
  const isToday = selectedDate === today;

  // Index attendance records by personId for O(1) lookup
  const recordsById = useMemo(() => {
    const map = {};
    attendanceRecords.forEach(r => { map[r.personId] = r; });
    return map;
  }, [attendanceRecords]);

  // Build the expected roster from schedule-app data
  const expectedPeople = useMemo(() => {
    const people = [];

    staff.forEach(s => {
      const absentAll = s.absentFullDay || (s.absentAM && s.absentPM);
      people.push({
        // Key must match what _mapRecord produces for LinkedPerson records
        personId:    `aad-email-${s.email.toLowerCase()}`,
        aadId:       s.id,
        personName:  s.name,
        personType:  'staff',
        role:        s.role || '',
        department:  s.department || '',
        email:       s.email || '',
        scheduleAbsent: absentAll ? 'full' : s.absentAM ? 'am' : s.absentPM ? 'pm' : null,
        expectedArrivalTime: s.absentAMArrivalTime || '',
        expectedDepartureTime: s.absentPMDepartureTime || '',
        outOfSession: s.outOfSessionFullDay,
      });
    });

    clients.forEach(c => {
      const absentAll = c.absentFullDay || (c.absentAM && c.absentPM);
      people.push({
        personId:    `client-${c.id}`,
        personId_sp: c.id,
        personName:  c.name,
        personType:  'client',
        program:     c.program || '',
        scheduleAbsent: absentAll ? 'full' : c.absentAM ? 'am' : c.absentPM ? 'pm' : null,
      });
    });

    return people;
  }, [staff, clients]);

  // Merge expected roster with real check-in records
  const mergedPeople = useMemo(() => {
    const expectedIds = new Set(expectedPeople.map(p => p.personId));
    const merged = expectedPeople.map(p => ({
      ...p,
      record:       recordsById[p.personId] || null,
      isCheckedIn:  recordsById[p.personId]?.isCheckedIn ?? false,
      checkInTime:  recordsById[p.personId]?.checkInTime ?? null,
      checkOutTime: recordsById[p.personId]?.checkOutTime ?? null,
    }));

    // Append walk-in visitors not on the expected roster
    attendanceRecords
      .filter(r => !expectedIds.has(r.personId))
      .forEach(r => merged.push({
        personId:     r.personId,
        personName:   r.personName,
        personType:   r.personType,
        role:         '',
        scheduleAbsent: null,
        record:       r,
        isCheckedIn:  r.isCheckedIn,
        checkInTime:  r.checkInTime,
        checkOutTime: r.checkOutTime,
        purpose:      r.purpose,
        host:         r.host,
        organization: r.organization,
      }));

    return merged;
  }, [expectedPeople, attendanceRecords, recordsById]);

  // Filter by search and mode
  const filtered = useMemo(() => {
    let result = mergedPeople;
    
    // Apply client filter
    if (filterMode === 'clients') {
      result = result.filter(p => p.personType === 'client');
    }
    
    // Apply search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.personName.toLowerCase().includes(q) ||
        p.personType.toLowerCase().includes(q) ||
        (p.role || '').toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [mergedPeople, search, filterMode]);

  // Calculate "in building at time X" for historical point-in-time queries
  const atTimeResults = useMemo(() => {
    if (isToday || !pointInTime) return null;
    
    // Parse the selected time
    const [hours, minutes] = pointInTime.split(':').map(Number);
    const targetTime = new Date(`${selectedDate}T${pointInTime}:00`);
    
    // Filter to people who were in the building at that specific time
    const inAtTime = mergedPeople.filter(p => {
      if (!p.checkInTime) return false;
      const checkIn = new Date(p.checkInTime);
      if (checkIn > targetTime) return false; // Hadn't arrived yet
      
      // If they checked out before the target time, they weren't there
      if (p.checkOutTime) {
        const checkOut = new Date(p.checkOutTime);
        if (checkOut <= targetTime) return false;
      }
      return true;
    });
    
    return {
      total: inAtTime.length,
      staff: inAtTime.filter(p => p.personType === 'staff').length,
      clients: inAtTime.filter(p => p.personType === 'client').length,
      visitors: inAtTime.filter(p => !['staff', 'client'].includes(p.personType)).length,
      people: inAtTime,
    };
  }, [mergedPeople, pointInTime, selectedDate, isToday]);

  const currentlyIn = useMemo(() => mergedPeople.filter(p => p.isCheckedIn), [mergedPeople]);

  const occupancy = useMemo(() => ({
    total:    currentlyIn.length,
    staff:    currentlyIn.filter(p => p.personType === 'staff').length,
    clients:  currentlyIn.filter(p => p.personType === 'client').length,
    visitors: currentlyIn.filter(p => !['staff', 'client'].includes(p.personType)).length,
  }), [currentlyIn]);

  const staffRows   = filtered.filter(p => p.personType === 'staff');
  const clientRows  = filtered.filter(p => p.personType === 'client');
  const visitorRows = filtered.filter(p => !['staff', 'client'].includes(p.personType));

  // ─── Manual visitor check-in ───────────────────────────────────────────────

  const handleAddVisitor = async () => {
    if (!visitorForm.name.trim() || addingVisitor) return;
    setAddingVisitor(true);
    const visitorId = `visitor-${Date.now()}`;
    try {
      await onCheckIn({
        personId:     visitorId,
        personId_sp:  0,
        personName:   visitorForm.name.trim(),
        personType:   visitorForm.type,
        purpose:      '',
        host:         visitorForm.host,
        organization: visitorForm.organization,
      });
    } catch { /* optimistic */ }
    setVisitorForm({ name: '', type: 'visitor', host: '', organization: '' });
    setShowAddVisitor(false);
    setAddingVisitor(false);
  };

  // ─── Row render ────────────────────────────────────────────────────────────

  const renderRow = (person) => {
    const absent = person.scheduleAbsent === 'full';
    const absentAM = person.scheduleAbsent === 'am';
    const absentPM = person.scheduleAbsent === 'pm';
    const arrivalLabel = person.expectedArrivalTime ? fmtTimeOfDay(person.expectedArrivalTime) : '';
    const departureLabel = person.expectedDepartureTime ? fmtTimeOfDay(person.expectedDepartureTime) : '';

    return (
      <tr
        key={person.personId}
        className={`border-b border-gray-100 transition-colors text-sm ${
          person.isCheckedIn ? 'bg-green-50 hover:bg-green-100'
          : absent           ? 'bg-red-50   hover:bg-red-100'
          :                    'hover:bg-gray-50'
        }`}
      >
        {/* Name + meta */}
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
              person.isCheckedIn ? 'bg-green-500'
              : absent           ? 'bg-red-400'
              :                    'bg-gray-300'
            }`} />
            <div>
              <div className="font-medium text-gray-900">{person.personName}</div>
              <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                {person.role && <span>{person.role}</span>}
                {person.department && <span>· {person.department}</span>}
                {person.program && <span>{person.program}</span>}
                {person.organization && <span>· {person.organization}</span>}
                {person.host && <span>· Host: {person.host}</span>}
                {person.purpose && <span>· {person.purpose}</span>}
              </div>
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="py-3 px-4 hidden sm:table-cell">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[person.personType] || TYPE_BADGE.visitor}`}>
            {TYPE_LABEL[person.personType] || person.personType}
          </span>
        </td>

        {/* Status */}
        <td className="py-3 px-4">
          {person.isCheckedIn ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Check className="w-3 h-3" /> In Building
            </span>
          ) : absent ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <UserX className="w-3 h-3" /> Absent
            </span>
          ) : absentAM ? (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              {arrivalLabel ? `Absent AM · Arrives ${arrivalLabel}` : 'Absent AM'}
            </span>
          ) : absentPM ? (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              {departureLabel ? `Absent PM · Leaves ${departureLabel}` : 'Absent PM'}
            </span>
          ) : person.checkOutTime ? (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Checked Out</span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">Not Arrived</span>
          )}
        </td>

        {/* Check-in time */}
        <td className="py-3 px-4 text-gray-500 hidden md:table-cell">
          {person.checkInTime ? fmt(person.checkInTime) : '—'}
        </td>

        {/* Check-out time */}
        <td className="py-3 px-4 text-gray-500 hidden md:table-cell">
          {person.checkOutTime
            ? fmt(person.checkOutTime)
            : person.isCheckedIn
            ? <span className="text-green-600 text-xs">Still in</span>
            : '—'}
        </td>

        {/* Action (today only) */}
        {isToday && (
          <td className="py-3 px-4">
            {person.isCheckedIn ? (
              <button
                onClick={() => onCheckOut(person.personId)}
                className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-xs font-semibold hover:bg-orange-200 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                <LogOut className="w-3 h-3" /> Check Out
              </button>
            ) : (
              <button
                onClick={() => onCheckIn(person)}
                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                <LogIn className="w-3 h-3" /> Check In
              </button>
            )}
          </td>
        )}
      </tr>
    );
  };

  // ─── Section table ─────────────────────────────────────────────────────────

  const renderSection = (rows, label, colorClass) => {
    const inCount = rows.filter(p => p.isCheckedIn).length;
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className={`px-4 py-3 border-b flex items-center justify-between ${colorClass.header}`}>
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Users className="w-4 h-4" /> {label}
          </h3>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colorClass.badge}`}>
            {inCount} in building / {rows.length} total
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-xs text-gray-400 uppercase tracking-wide">
                <th className="py-2 px-4 text-left font-medium">Name</th>
                <th className="py-2 px-4 text-left font-medium hidden sm:table-cell">Type</th>
                <th className="py-2 px-4 text-left font-medium">Status</th>
                <th className="py-2 px-4 text-left font-medium hidden md:table-cell">Check In</th>
                <th className="py-2 px-4 text-left font-medium hidden md:table-cell">Check Out</th>
                {isToday && <th className="py-2 px-4 text-left font-medium">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0
                ? rows.map(renderRow)
                : (
                  <tr>
                    <td colSpan={isToday ? 6 : 5} className="py-6 text-center text-gray-400 text-sm">
                      No records.
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Occupancy stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'In Building',   value: occupancy.total,    colors: 'bg-blue-50 border-blue-200 text-blue-700'   },
          { label: 'Staff',         value: occupancy.staff,    colors: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
          { label: 'Clients',       value: occupancy.clients,  colors: 'bg-green-50 border-green-200 text-green-700' },
          { label: 'Visitors',      value: occupancy.visitors, colors: 'bg-purple-50 border-purple-200 text-purple-700' },
        ].map(card => (
          <div key={card.label} className={`border rounded-xl p-4 text-center ${card.colors}`}>
            <div className="text-4xl font-bold">{card.value}</div>
            <div className="text-xs font-medium mt-1 opacity-80">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Filter toggle */}
        <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setFilterMode('all')}
            className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
              filterMode === 'all'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" /> All
          </button>
          <button
            onClick={() => setFilterMode('clients')}
            className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
              filterMode === 'clients'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" /> Clients Only
          </button>
        </div>

        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={selectedDate}
            max={today}
            onChange={e => { onDateChange(e.target.value); setPointInTime(''); }}
            className="text-sm text-gray-700 outline-none bg-transparent"
          />
        </div>

        {/* Point-in-time filter for historical dates */}
        {!isToday && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
            <Clock className="w-4 h-4 text-amber-600" />
            <input
              type="time"
              value={pointInTime}
              onChange={e => setPointInTime(e.target.value)}
              className="text-sm text-amber-700 outline-none bg-transparent font-medium"
              placeholder="At time..."
            />
            {pointInTime && (
              <button onClick={() => setPointInTime('')} className="text-amber-500 hover:text-amber-700">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 flex-1 min-w-40">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, role…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-sm outline-none bg-transparent flex-1"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <button
          onClick={onRefresh}
          className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        {isToday && (
          <button
            onClick={() => setShowAddVisitor(true)}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add Visitor
          </button>
        )}
      </div>

      {/* Add Visitor Modal */}
      {showAddVisitor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Check In Visitor</h3>
              <button
                onClick={() => setShowAddVisitor(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={visitorForm.name}
                  onChange={e => setVisitorForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Visitor's full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visitor Type</label>
                <select
                  value={visitorForm.type}
                  onChange={e => setVisitorForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {VISITOR_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Who are they visiting?</label>
                <input
                  type="text"
                  value={visitorForm.host}
                  onChange={e => setVisitorForm(f => ({ ...f, host: e.target.value }))}
                  placeholder="Staff name or client initials"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <input
                  type="text"
                  value={visitorForm.organization}
                  onChange={e => setVisitorForm(f => ({ ...f, organization: e.target.value }))}
                  placeholder="Company or organization name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddVisitor(false)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddVisitor}
                disabled={!visitorForm.name.trim() || addingVisitor}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                {addingVisitor ? 'Saving…' : 'Check In'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historical notice */}
      {!isToday && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 flex items-center gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          Viewing historical record for <strong className="ml-1">{formatDateDisplay(selectedDate)}</strong>.
          {pointInTime ? (
            <span className="ml-1">Showing who was in building at <strong>{pointInTime}</strong>.</span>
          ) : (
            <span className="ml-1">Use the time picker to see who was in at a specific time.</span>
          )}
        </div>
      )}

      {/* Point-in-time stats */}
      {atTimeResults && (
        <div className="bg-amber-100 border border-amber-300 rounded-xl p-4">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            In Building at {pointInTime} on {formatDateDisplay(selectedDate)}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total',    value: atTimeResults.total,    colors: 'bg-amber-200 text-amber-900' },
              { label: 'Staff',    value: atTimeResults.staff,    colors: 'bg-indigo-100 text-indigo-700' },
              { label: 'Clients',  value: atTimeResults.clients,  colors: 'bg-green-100 text-green-700' },
              { label: 'Visitors', value: atTimeResults.visitors, colors: 'bg-purple-100 text-purple-700' },
            ].map(card => (
              <div key={card.label} className={`rounded-lg p-3 text-center ${card.colors}`}>
                <div className="text-3xl font-bold">{card.value}</div>
                <div className="text-xs font-medium mt-0.5 opacity-80">{card.label}</div>
              </div>
            ))}
          </div>
          {atTimeResults.people.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm font-medium text-amber-800 cursor-pointer hover:underline">
                View all {atTimeResults.total} people
              </summary>
              <ul className="mt-2 text-sm text-amber-900 grid grid-cols-2 sm:grid-cols-3 gap-1">
                {atTimeResults.people.map(p => (
                  <li key={p.personId} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${
                      p.personType === 'staff' ? 'bg-indigo-500'
                      : p.personType === 'client' ? 'bg-green-500'
                      : 'bg-purple-500'
                    }`} />
                    {p.personName}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* No data state */}
      {mergedPeople.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-14 h-14 mx-auto mb-4 opacity-20" />
          <p className="text-lg font-medium text-gray-500">No data loaded</p>
          <p className="text-sm mt-1">
            {isToday
              ? 'Staff and clients are loaded from your SharePoint lists. If you see nothing, check that the Staff and Clients lists have active records.'
              : 'No attendance records were saved for this date.'}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Search className="w-14 h-14 mx-auto mb-4 opacity-20" />
          <p className="text-lg font-medium text-gray-500">No matches found</p>
          <p className="text-sm mt-1">
            {filterMode === 'clients' 
              ? 'No clients match your search criteria.'
              : 'Try adjusting your search or filter.'}
          </p>
        </div>
      ) : (
        <>
          {filterMode === 'all' && staffRows.length > 0 && renderSection(staffRows, 'Staff', {
            header: 'bg-indigo-50 text-indigo-800',
            badge: 'bg-indigo-100 text-indigo-700',
          })}
          {clientRows.length > 0 && renderSection(clientRows, 'Clients', {
            header: 'bg-green-50 text-green-800',
            badge: 'bg-green-100 text-green-700',
          })}
          {filterMode === 'all' && visitorRows.length > 0 && renderSection(visitorRows, 'Visitors & External', {
            header: 'bg-purple-50 text-purple-800',
            badge: 'bg-purple-100 text-purple-700',
          })}
        </>
      )}
    </div>
  );
}
